import React from 'react';
import { Shield, CheckCircle2, Download, Copy, ExternalLink } from 'lucide-react';
import { bytesToHex } from '@noble/curves/abstract/utils';

interface MerkleReceipt {
  rootHash: string;
  treeSize: number;
  timestamp: number;
  verified: boolean;
  fileName?: string;
  fileSize?: number;
  transferId?: string;
}

interface MerkleReceiptProps {
  receipt: MerkleReceipt | null;
  onClose?: () => void;
  onVerify?: () => void;
  onExport?: () => void;
}

export const MerkleReceiptDisplay: React.FC<MerkleReceiptProps> = ({ 
  receipt, 
  onClose, 
  onVerify,
  onExport 
}) => {
  if (!receipt) return null;

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      // TODO: Add toast notification
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const formatFileSize = (bytes: number) => {
    const units = ['B', 'KB', 'MB', 'GB', 'TB'];
    let size = bytes;
    let unitIndex = 0;
    while (size >= 1024 && unitIndex < units.length - 1) {
      size /= 1024;
      unitIndex++;
    }
    return `${size.toFixed(2)} ${units[unitIndex]}`;
  };

  const formatDate = (timestamp: number) => {
    return new Date(timestamp).toLocaleString();
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6 max-w-md mx-auto">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Shield className="w-6 h-6 text-green-500" />
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
            Transfer Receipt
          </h3>
        </div>
        {receipt.verified && (
          <CheckCircle2 className="w-5 h-5 text-green-500" />
        )}
      </div>

      <div className="space-y-3">
        {receipt.fileName && (
          <div>
            <label className="text-sm text-gray-500 dark:text-gray-400">File</label>
            <p className="font-mono text-sm text-gray-900 dark:text-white">
              {receipt.fileName}
            </p>
          </div>
        )}

        {receipt.fileSize && (
          <div>
            <label className="text-sm text-gray-500 dark:text-gray-400">Size</label>
            <p className="font-mono text-sm text-gray-900 dark:text-white">
              {formatFileSize(receipt.fileSize)}
            </p>
          </div>
        )}

        <div>
          <label className="text-sm text-gray-500 dark:text-gray-400">Root Hash</label>
          <div className="flex items-center gap-2">
            <p className="font-mono text-xs text-gray-900 dark:text-white truncate flex-1">
              {receipt.rootHash}
            </p>
            <button
              onClick={() => copyToClipboard(receipt.rootHash)}
              className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded"
              title="Copy hash"
            >
              <Copy className="w-4 h-4 text-gray-500" />
            </button>
          </div>
        </div>

        <div>
          <label className="text-sm text-gray-500 dark:text-gray-400">Merkle Tree Size</label>
          <p className="font-mono text-sm text-gray-900 dark:text-white">
            {formatFileSize(receipt.treeSize)}
          </p>
        </div>

        <div>
          <label className="text-sm text-gray-500 dark:text-gray-400">Timestamp</label>
          <p className="font-mono text-sm text-gray-900 dark:text-white">
            {formatDate(receipt.timestamp)}
          </p>
        </div>

        <div className="pt-2 border-t border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <span className="text-sm text-gray-500 dark:text-gray-400">Status</span>
            <span className={`text-sm font-medium ${
              receipt.verified 
                ? 'text-green-600 dark:text-green-400' 
                : 'text-yellow-600 dark:text-yellow-400'
            }`}>
              {receipt.verified ? 'Verified' : 'Pending Verification'}
            </span>
          </div>
        </div>
      </div>

      <div className="flex gap-2 mt-4">
        {!receipt.verified && onVerify && (
          <button
            onClick={onVerify}
            className="flex-1 px-3 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 transition-colors text-sm font-medium"
          >
            Verify
          </button>
        )}
        
        {onExport && (
          <button
            onClick={onExport}
            className="flex-1 px-3 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors text-sm font-medium flex items-center justify-center gap-1"
          >
            <Download className="w-4 h-4" />
            Export
          </button>
        )}

        {onClose && (
          <button
            onClick={onClose}
            className="px-3 py-2 text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 text-sm"
          >
            Close
          </button>
        )}
      </div>
    </div>
  );
};

// Compact receipt badge for transfer list
export const MerkleReceiptBadge: React.FC<{ hasReceipt: boolean; verified?: boolean }> = ({ 
  hasReceipt, 
  verified 
}) => {
  if (!hasReceipt) return null;

  return (
    <div className="inline-flex items-center gap-1 px-2 py-1 bg-green-50 dark:bg-green-900/20 rounded-full">
      <Shield className="w-3 h-3 text-green-600 dark:text-green-400" />
      <span className="text-xs font-medium text-green-700 dark:text-green-300">
        {verified ? 'Receipt' : 'Receipt Available'}
      </span>
    </div>
  );
};

// Export receipt as JSON
export const exportReceipt = (receipt: MerkleReceipt, fileName: string = 'transfer-receipt.json') => {
  const receiptData = {
    ...receipt,
    exportedAt: Date.now(),
    version: '1.0.0'
  };
  
  const blob = new Blob([JSON.stringify(receiptData, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};
